/**
 * Phase 2 Projects — 廣告專案容器型別
 * -----------------------------------------------------------------------------
 * 對齊後端 ``/hai-creative/api/projects/*`` 5 endpoints（純 CRUD）。Phase 2 純
 * metadata；Phase 3 開始加 jobs 子集合。
 *
 * Phase 2 共識：
 *   - ``brand_kit_id``：純字串 optional；**Phase 2 不跨檢**（填不存在也存得進去）
 *   - ``brand_kit_id``：PATCH **不接受**（想換重建一份）
 *   - ``project_id``：immutable（同 brand_kit_id 設計）
 *   - 軟刪除預設 hide；``?include_deleted=true`` opt-in（admin / audit 用）
 *
 * @remarks
 * **命名衝突警告**：``services.graphRepo`` 內部也用「project」一詞（指 canvas graph
 * 的 ``projectId``，e.g. ``proj_demo_tree``），但跟本檔的 ``ProjectsService`` 是不
 * 同 domain。R14-15 階段共存；未來統一命名（可能把 graphRepo 內部 rename 成
 * ``graphId``）。
 *
 * @see docs/PROJECTS.md
 */

import { mapCoverDto, type Cover, type CoverDto } from './cover';

// ─── DTO（snake_case）────────────────────────────────────────────────────────

export interface ProjectSettingsDto {
  aspect_ratios: string[];
  languages: string[];
  default_template_id: string | null;
}

export interface ProjectDto {
  /**
   * R48.x — BE 真實 wire 是 `pid`(對齊 R45 Beta v0.0.1 / 5-Layer 命名:`pid` /
   * `gid` / `cuid` / `bkid` short ID 慣例);舊 schema 的 `project_id` 已 deprecate。
   */
  pid: string;
  name: string;
  description: string | null;
  brand_kit_id: string | null;
  default_settings: ProjectSettingsDto;
  /**
   * R48.x — BE 真實 wire 是 `created_at` / `updated_at`(對齊 Beta v0.0.1 BE 全套
   * timestamp naming);舊 `created_datetime` 已 deprecate。
   */
  created_at: string;
  updated_at: string;
  /** BE soft-delete 欄位;若 BE response 省略 → undefined(視同 null)。 */
  deleted_at?: string | null;
  // R45 Beta v0.0.1 — root collection projects + group tenancy(spec §3.5 + §8)
  /** Project creator(IMMUTABLE — 不接受 PATCH)。 */
  owner_uid?: string;
  /** Project group tenancy(IMMUTABLE — 換 group 必須 DELETE + 重建)。 */
  group_id?: string;
  /** v0.0.1 only `"owner_and_group_owner"`(privacy-first;見 5-Layer §3.5)。 */
  visibility?: string;
  /**
   * T12(docs/50)— `GET /projects` list item 多帶的素材數(`projects/{pid}/assets`
   * count() aggregation,`≥0`)。**僅 list 端點回此欄**;單筆 get/create/update/
   * remove 回純 `ProjectResponse` 不含 → optional,mapper 缺值回退 0。
   */
  asset_count?: number;
  /**
   * R245(BE docs/92)— 封面三態。BE `ProjectResponse.cover` 有
   * `default_factory=CoverSpec` → **上線後必出**,舊 doc 也會被讀成 `kind:"none"`,
   * 不需要 migration。
   *
   * 這裡仍宣告成 optional 是因為**過渡期**:BE 這批交付時還沒部到 dev,FE 先上線
   * 的期間拿到的回應根本沒有這個 key。`mapCoverDto(undefined)` 回 `none` → 走既有
   * 的品牌色漸層 fallback,畫面零異常。BE 部署後這個 `?` 可以拿掉(留著也無害)。
   */
  cover?: CoverDto;
}

// ─── 前端 camelCase ──────────────────────────────────────────────────────────

export interface ProjectSettings {
  aspectRatios: string[];
  languages: string[];
  defaultTemplateId: string | null;
}

export interface Project {
  projectId: string;
  name: string;
  description: string | null;
  /**
   * 引用 brand kit ID（純字串）。Phase 2 後端**不**驗證此 ID 是否真的存在 —
   * 請求 success 不代表 brand_kit 真存在。前端要在用戶選擇前先列 brand_kits 給選。
   */
  brandKitId: string | null;
  defaultSettings: ProjectSettings;
  createdDatetime: string;
  updatedDatetime: string;
  deletedDatetime: string | null;
  // R45 Beta v0.0.1 — root collection projects + group tenancy(spec §3.5 + §8)
  //
  // @reserved R45+ versioned-permissions 權限控制預留(DTO passthrough 成本零,勿擅刪)。
  //   下列三欄 mapper 從 BE response 寫入(見 mapProjectDto),但目前 0 consumer —
  //   權限/可見性控制尚未做。保留以利日後 owner/group/visibility 判定;passthrough 不增成本。
  /** Project creator;`null` 表示舊 BE 端尚未回欄。 */
  ownerUid: string | null;
  /** Project group tenancy;`null` 表示舊 BE 端尚未回欄。 */
  groupId: string | null;
  /** v0.0.1 only `"owner_and_group_owner"`;`null` 表示舊 BE 端尚未回欄。 */
  visibility: string | null;
  /**
   * T12(docs/50)— 該 project 下已生成素材數。僅 `list()` 端點回真值;單筆
   * get/create/update/remove 回退 `0`(BE 不在純 entity 端點回此欄,見 `ProjectDto`)。
   */
  assetCount: number;
  /** R245 — 封面三態(`none` / `color` / `image`);詳見 `types/cover.ts`。 */
  cover: Cover;
}

// ─── DTO → camelCase mapping ─────────────────────────────────────────────────

function mapProjectSettingsDto(dto: ProjectSettingsDto): ProjectSettings {
  return {
    aspectRatios: dto.aspect_ratios,
    languages: dto.languages,
    defaultTemplateId: dto.default_template_id,
  };
}

export function mapProjectDto(dto: ProjectDto): Project {
  return {
    projectId: dto.pid,
    name: dto.name,
    description: dto.description,
    brandKitId: dto.brand_kit_id,
    defaultSettings: mapProjectSettingsDto(dto.default_settings),
    createdDatetime: dto.created_at,
    updatedDatetime: dto.updated_at,
    deletedDatetime: dto.deleted_at ?? null,
    ownerUid: dto.owner_uid ?? null,
    groupId: dto.group_id ?? null,
    visibility: dto.visibility ?? null,
    assetCount: dto.asset_count ?? 0,
    cover: mapCoverDto(dto.cover),
  };
}
